import React from "react";
import { observer } from "mobx-react";
import { computed, observable } from "mobx";
import { FormSpy } from "react-final-form";

import { Button, Typography } from "@material-ui/core";

import InAppResourcesStore from "stores/InAppResourcesStore";
import ActiveProgramDictionary from "stores/dictionary/ActiveProgramDictionary";

import FormControl, {
  TextFieldControl,
  CheckboxGroupWithSwitchFieldControl,
} from "components/FormControl";
import SubmitButtonControl from "components/FormControl/SubmitButtonControl";
import LoadingButton from "components/LoadingButton";
import Row from "components/Form/Row";
import Loader from "components/Layout/Loader";
import Popup from "components/Popup";
import ConfirmationDialog from "components/ConfirmationDialog";
import PublishStatus from "components/PublishStatus";

import { lengthValidator } from "utils/validators";

import { PUBLISH_STATUSES } from "constants/models";

import {
  Container,
  TitleContainer,
  ControlRow,
  Buttons,
  ButtonContainer,
} from "./styles";

type FormSubmitData = {
  values: any;
};

type Props = {
  isLoading: boolean;
  onClose: () => void;
  onChangeIsDirty: (params: { dirty: boolean }) => void;
  onSubmit: (data: FormSubmitData) => void;
  initialValues?: any;
  isFormLoading?: boolean;
};

@observer
class CategoryForm extends React.Component<Props> {
  @observable updateConfirmationIsOpened: boolean = false;

  get isEditMode() {
    const { initialValues } = this.props;
    return !!initialValues;
  }

  get formTitle() {
    return !this.isEditMode ? "Add Category" : "Edit Category";
  }

  @computed get activeProgramsOptions() {
    return ActiveProgramDictionary.options.map(({ value, label }) => ({
      value: { id: value },
      label,
    }));
  }

  @computed get initialValues() {
    return this.isEditMode ? InAppResourcesStore.categoryDetails?.formData : {};
  }

  @computed get publishStatus() {
    const publishStatus =
      InAppResourcesStore.categoryDetails?.formData?.publishStatus;

    return publishStatus;
  }

  componentDidMount() {
    const { initialValues } = this.props;

    if (initialValues?.id) {
      InAppResourcesStore.fetchCategoryDetails(initialValues.id);
    }
  }

  componentWillUnmount() {
    InAppResourcesStore.resetCategoryDetails();
  }

  openUpdateConfirmation = () => {
    this.updateConfirmationIsOpened = true;
  };

  closeUpdateConfirmation = () => {
    this.updateConfirmationIsOpened = false;
  };

  confirmUpdate = (values: any) => () => {
    const { onSubmit } = this.props;

    this.closeUpdateConfirmation();
    onSubmit(values);
  };

  onSubmit = (values: any) => {
    const { onSubmit } = this.props;

    const shouldShowUpdateConfirmation =
      this.publishStatus &&
      [
        PUBLISH_STATUSES.PUBLISHED,
        PUBLISH_STATUSES.SCHEDULE_FOR_REMOVE,
      ].includes(this.publishStatus);

    shouldShowUpdateConfirmation
      ? this.openUpdateConfirmation()
      : onSubmit(values);
  };

  render() {
    const { onClose, isFormLoading, onChangeIsDirty } = this.props;

    return (
      <FormControl onSubmit={this.onSubmit} initialValues={this.initialValues}>
        {this.updateConfirmationIsOpened && (
          <FormSpy subscription={{ values: true }}>
            {({ values }) => (
              <Popup onClose={this.closeUpdateConfirmation} withoutExitButton>
                <ConfirmationDialog
                  title="Save Changes to Production"
                  text="All changes made will be published to production. Are you sure you want to proceed?"
                  acceptText="Proceed"
                  declineText="Cancel"
                  onAccept={this.confirmUpdate(values)}
                  onDecline={this.closeUpdateConfirmation}
                />
              </Popup>
            )}
          </FormSpy>
        )}
        <FormSpy subscription={{ dirty: true }} onChange={onChangeIsDirty} />
        <Container>
          <TitleContainer>
            <Typography variant="h1" color="textPrimary">
              {this.formTitle}
            </Typography>
            {this.isEditMode && this.publishStatus && (
              <PublishStatus status={this.publishStatus} />
            )}
          </TitleContainer>

          {!isFormLoading ? (
            <>
              <Row>
                <TextFieldControl
                  variant="outlined"
                  margin="none"
                  label="Title"
                  name="title"
                  placeholder="Enter title"
                  validate={lengthValidator(100)}
                  fullWidth
                  required
                />
              </Row>

              <Row>
                <TextFieldControl
                  variant="outlined"
                  margin="none"
                  name="note"
                  label="Note"
                  placeholder="Enter note"
                  validate={lengthValidator(256)}
                  fullWidth
                />
              </Row>

              <ControlRow>
                <CheckboxGroupWithSwitchFieldControl
                  name="activePrograms"
                  title="Access"
                  options={this.activeProgramsOptions}
                  switchLabel="Preview only"
                  disabledSwitch
                  inline
                  multi
                  required
                />
              </ControlRow>
            </>
          ) : (
            <Row>
              <Loader />
            </Row>
          )}
        </Container>

        <Buttons>
          <ButtonContainer>
            <Button
              variant="outlined"
              color="primary"
              onClick={onClose}
              fullWidth
            >
              Cancel
            </Button>
          </ButtonContainer>
          <ButtonContainer>
            <SubmitButtonControl render={this.renderButton} />
          </ButtonContainer>
        </Buttons>
      </FormControl>
    );
  }

  renderButton = ({ onClick }: { onClick: () => void }) => (
    <LoadingButton
      onClick={onClick}
      variant="contained"
      color="primary"
      isLoading={this.props.isLoading}
      fullWidth
    >
      {!this.isEditMode ? "Add" : "Save"}
    </LoadingButton>
  );
}

export default CategoryForm;
